import {
  type Policy,
  type PolicyMetadata,
  type SessionV2,
} from "@decentriq/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type SnackbarKey } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiCore } from "contexts";
import {
  type MediaDataRoomComputeRequestKey,
  MediaDataRoomJobInput,
} from "features/mediaDataRoom/hooks/v3";
import {
  type BaseAudienceDatasetsHashes,
  PublishedDatasetsHashes,
} from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { type DatasetLeafName, type DatasetsResult } from "../models";

type DatasetsHookResult = DatasetsResult;

interface DatasetsHookPayload {
  dataRoomId: string;
  session: SessionV2 | undefined;
  refetchDataLab?: () => void;
}

const useDatasets = ({
  dataRoomId,
  session,
  refetchDataLab,
}: DatasetsHookPayload): DatasetsHookResult => {
  const { client, getVerificationKey } = useApiCore();
  const queryClient = useQueryClient();
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];
  const queryKey = useMemo(
    () =>
      MediaDataRoomJobInput.create(
        "retrievePublishedDatasets" as MediaDataRoomComputeRequestKey,
        dataRoomId
      )
        .withFetchResultsSubKey()
        .buildQueryKey(),
    [dataRoomId]
  );
  const updateAudiencesDatasetHash = useCallback(
    (audiencesDatasetHash: string | null) =>
      queryClient.setQueryData<PublishedDatasetsHashes>(queryKey, (oldData) =>
        (oldData ?? new PublishedDatasetsHashes()).updateAudiencesDatasetHash(
          audiencesDatasetHash
        )
      ),
    [queryKey, queryClient]
  );
  const updateAdvertiserDatasetHash = useCallback(
    (advertiserDatasetHash: string | null) => {
      queryClient.setQueryData<PublishedDatasetsHashes>(queryKey, (oldData) =>
        (oldData ?? new PublishedDatasetsHashes()).updateAdvertiserDatasetHash(
          advertiserDatasetHash
        )
      );
    },
    [queryKey, queryClient]
  );
  const updatePublishedDatasetsHashes = useCallback(
    (value: PublishedDatasetsHashes | null) => {
      queryClient.setQueryData<PublishedDatasetsHashes>(
        queryKey,
        () => value ?? new PublishedDatasetsHashes()
      );
    },
    [queryKey, queryClient]
  );
  const updatePublisherDatasetsHashes = useCallback(
    (publisherDatasetsHashes: BaseAudienceDatasetsHashes | null) => {
      queryClient.setQueryData<PublishedDatasetsHashes>(queryKey, (oldData) =>
        (
          oldData ?? new PublishedDatasetsHashes()
        ).updatePublisherDatasetsHashes(
          publisherDatasetsHashes ??
            PublishedDatasetsHashes.defaultPublisherDatasetsHashes
        )
      );
      refetchDataLab?.();
    },
    [queryKey, queryClient, refetchDataLab]
  );
  const getDatasetHashFromPolicies = useCallback(
    (
      targetLeafName: DatasetLeafName,
      policies: Record<string, Policy>
    ): string | null =>
      (
        Object.values(policies).find(
          (policy) =>
            policy.status === "ok" && policy.target_leaf === targetLeafName
        ) as PolicyMetadata | undefined
      )?.manifest_hash_hex ?? null,
    []
  );
  const handleFetch = useCallback(async () => {
    const verificationKey = await getVerificationKey(session);
    const mediaDataRoom = await client.getDataRoom(dataRoomId, verificationKey);
    if (mediaDataRoom.state.kind !== "media") {
      throw new Error("Requested data clean room is not media data room");
    }
    const data = mediaDataRoom.state;
    const policiesMetadata = await client.getDataRoomPolicies(
      dataRoomId,
      data.policies.map(({ policyId }) => policyId)
    );
    return new PublishedDatasetsHashes(
      getDatasetHashFromPolicies("audiences", policiesMetadata),
      {
        demographicsDatasetHash: getDatasetHashFromPolicies(
          "demographics",
          policiesMetadata
        ),
        embeddingsDatasetHash: getDatasetHashFromPolicies(
          "embeddings",
          policiesMetadata
        ),
        matchingDatasetHash: getDatasetHashFromPolicies(
          "matching",
          policiesMetadata
        ),
        segmentsDatasetHash: getDatasetHashFromPolicies(
          "segments",
          policiesMetadata
        ),
      },
      null
    );
  }, [
    session,
    client,
    dataRoomId,
    getDatasetHashFromPolicies,
    getVerificationKey,
  ]);
  const queryResult = useQuery({
    enabled: Boolean(session),
    queryFn: handleFetch,
    queryKey,
  });
  useEffect(() => {
    if (queryResult.error) {
      const snackbarId = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          queryResult.error,
          `Unable to fetch datasets`
        )
      );
      setErrorSnackbarId(snackbarId);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [enqueueSnackbar, closeSnackbar, setErrorSnackbarId, queryResult.error]);
  return useMemo(
    () => ({
      ...queryResult,
      data: queryResult.data ?? new PublishedDatasetsHashes(),
      fetch: handleFetch,
      updateAdvertiserDatasetHash,
      updateAudiencesDatasetHash,
      updatePublishedDatasetsHashes,
      updatePublisherDatasetsHashes,
    }),
    [
      queryResult,
      updateAudiencesDatasetHash,
      updateAdvertiserDatasetHash,
      updatePublisherDatasetsHashes,
      updatePublishedDatasetsHashes,
      handleFetch,
    ]
  );
};

export default useDatasets;
